import React, { useState } from "react";
import "./image-gallery-component.scss";
import {
  Box,
  Modal,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  SvgIcon,
} from "@mui/material";
import {
  GridView,
  Close,
  KeyboardArrowRight,
  KeyboardArrowLeft,
} from "@mui/icons-material";
import ButtonNew from "../../../../common/components/button-new/button-new";
import { ImageGalleryProps } from "./image-gallery-component.types";
import { ReactComponent as NoImageIcon } from "../../../../assets/icons/no-img-icon.svg";

const ImageGalleryComponent: React.FC<ImageGalleryProps> = ({
  images,
  providerTitle,
}) => {
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleOpenModal = (index: number) => {
    setCurrentIndex(index);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const currentImage = images[currentIndex];
  const counter = `${currentIndex + 1}/${images.length}`;

  const MobileGallery = () => {
    const displayedImages = images.slice(0, 3);

    return (
      <Box className="image-gallery-mobile">
        {displayedImages.map((img, idx) => (
          <Box
            key={idx}
            className={`image-gallery-mobile__image ${
              idx === 0 ? "image-gallery-mobile__image--large" : ""
            }`}
            onClick={() => handleOpenModal(idx)}
          >
            <img src={img} alt={`Mobile gallery ${idx + 1}`} />
          </Box>
        ))}

        {/* Floating button */}
        <Box className="image-gallery-mobile__button-wrapper">
          <ButtonNew
            key={1}
            text="Show all the photos"
            icon={<GridView />}
            iconPosition="left"
            bgColor="#FFFFFF"
            textColor="#000000"
            borderColor="#000000"
            onClick={() => handleOpenModal(0)}
            iconColor="#000000"
            className="image-gallery-mobile__button"
          />
        </Box>
      </Box>
    );
  };

  // Desktop Gallery
  const DesktopGallery = () => {
    const hasImages = images && images.length > 0;

    // If no images — empty state
    if (!hasImages) {
      return (
        <Box className="image-gallery image-gallery--empty">
          <Box className="image-gallery__empty-box">
            <SvgIcon
              component={NoImageIcon}
              inheritViewBox
              sx={{
                width: "96px",
                height: "96px",
                marginBottom: "1rem",
                color: "#777",
              }}
            />
            <Typography>No Images Available</Typography>
          </Box>
        </Box>
      );
    }

    // If exactly 1 image
    if (images.length === 1) {
      return (
        <Box className="image-gallery image-gallery--single">
          <img
            src={images[0]}
            alt="Single"
            className="image-gallery__single-image"
            onClick={() => handleOpenModal(0)}
          />
        </Box>
      );
    }

    // If exactly 2 images — show them side-by-side
    if (images.length === 2) {
      return (
        <Box className="image-gallery image-gallery--two">
          {images.map((img, idx) => (
            <Box
              key={idx}
              className="image-gallery__two-image"
              onClick={() => handleOpenModal(idx)}
            >
              <img src={img} alt={`Image ${idx + 1}`} />
            </Box>
          ))}
        </Box>
      );
    }

    // If exactly 3 image
    if (images.length === 3) {
      return (
        <Box className="image-gallery image-gallery--three">
          {images.map((img, idx) => (
            <Box
              key={idx}
              className="image-gallery__three-image"
              onClick={() => handleOpenModal(idx)}
            >
              <img src={img} alt={`Image ${idx + 1}`} />
            </Box>
          ))}
        </Box>
      );
    }

    // Default (4–5 images)
    return (
      <Box className="image-gallery">
        <Box className="image-gallery__main-image">
          {images[0] && (
            <img
              src={images[0]}
              alt="Main"
              onClick={() => handleOpenModal(0)}
            />
          )}
        </Box>

        <Box className="image-gallery__side-images">
          {images.slice(1, 5).map((img, idx) => (
            <Box key={idx} className="image-gallery__side-image">
              <img
                src={img}
                alt={`Gallery ${idx + 1}`}
                onClick={() => handleOpenModal(idx + 1)}
              />
            </Box>
          ))}
        </Box>

        <Box className="image-gallery__footer">
          <ButtonNew
            key={1}
            text=" Show all the photos"
            icon={<GridView />}
            iconPosition="left"
            bgColor="#FFFFFF"
            textColor="#000000"
            borderColor="#000000"
            onClick={() => handleOpenModal(0)}
            iconColor="#000000"
            className="image-gallery__button"
          />
        </Box>
      </Box>
    );
  };

  return (
    <>
      {isMobile ? <MobileGallery /> : <DesktopGallery />}

      {/* Image Modal  */}
      <Modal
        open={open}
        onClose={handleCloseModal}
        className="custom-modal-image-component"
      >
        <Box className="image-modal">
          <Box className="image-modal__header">
            <Typography variant="h6" className="image-modal__title">
              {"Pictures of - " + providerTitle}
            </Typography>
            <IconButton
              onClick={handleCloseModal}
              className="image-modal__close"
            >
              <Close />
            </IconButton>
          </Box>

          <img
            src={currentImage}
            alt={providerTitle}
            className="image-modal__image"
          />

          <IconButton
            onClick={handlePrev}
            className="image-modal__nav-left"
            disabled={currentIndex === 0}
          >
            <KeyboardArrowLeft fontSize="small" />
          </IconButton>

          <IconButton
            onClick={handleNext}
            className="image-modal__nav-right"
            disabled={currentIndex === images.length - 1}
          >
            <KeyboardArrowRight fontSize="small" />
          </IconButton>

          <Typography variant="body2" className="image-modal__counter">
            {counter}
          </Typography>
        </Box>
      </Modal>
    </>
  );
};

export default ImageGalleryComponent;
